import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type {
  ESelectiveProcessAdmissionType,
  ESelectiveProcessStatus,
} from '@/shared/api/generated';
import {
  getApiV1CourseregistrationEnrollmentCourseregistrationidSelectiveProcessTabQueryKey,
  usePutApiV1CourseregistrationEnrollmentCourseregistrationidSelectiveProcessTab,
} from '@/shared/api/generated';

import type { AdmissionType, SelectiveProcess, SelectiveProcessStatus } from '../types/enrollment';

// Mapeamentos de domínio → API
const SELECTIVE_STATUS_TO_CODE: Record<SelectiveProcessStatus, ESelectiveProcessStatus> = {
  pending: 1,
  approved: 2,
  rejected: 3,
  waiting_exam: 5,
  waiting_interview: 4,
};

const ADMISSION_TYPE_TO_CODE: Record<AdmissionType, ESelectiveProcessAdmissionType> = {
  vestibular: 1,
  interview: 2,
  transfer: 3,
  other: 4,
};

export interface SelectiveProcessForm {
  readonly status: SelectiveProcessStatus;
  readonly admissionType: AdmissionType;
  readonly isEntranceExam: boolean;
  readonly attendedEntranceExam: boolean;
  readonly entranceExamDate: string;
  readonly entranceExamGrade: string;
  readonly essayGrade: string;
  readonly interviewDate: string;
  readonly interviewerName: string;
  readonly isDocumentationApproved: boolean;
  readonly documentationRejectReason: string;
}

/**
 * Hook para edição do processo seletivo de uma matrícula.
 * Gerencia estado interno do formulário e chama PUT selective-process-tab.
 *
 * @param enrollmentId - ID da matrícula (courseRegistrationId)
 * @param onSuccess - Callback chamado após salvar com sucesso
 */
export function useEditSelectiveProcess(enrollmentId: number, onSuccess: () => void) {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<SelectiveProcessForm>({
    status: 'pending',
    admissionType: 'vestibular',
    isEntranceExam: false,
    attendedEntranceExam: false,
    entranceExamDate: '',
    entranceExamGrade: '',
    essayGrade: '',
    interviewDate: '',
    interviewerName: '',
    isDocumentationApproved: false,
    documentationRejectReason: '',
  });

  const initForm = useCallback((sp: SelectiveProcess) => {
    setForm({
      status: sp.status,
      admissionType: sp.admissionType,
      isEntranceExam: sp.isEntranceExam ?? false,
      attendedEntranceExam: sp.isAttendEntranceExam,
      entranceExamDate: sp.entranceExamDate ?? '',
      entranceExamGrade: sp.entranceExamGrade != null ? String(sp.entranceExamGrade) : '',
      essayGrade: sp.essayGrade != null ? String(sp.essayGrade) : '',
      interviewDate: sp.interviewDate ?? '',
      interviewerName: sp.interviewerName ?? '',
      isDocumentationApproved: sp.isDocumentationApproved ?? false,
      documentationRejectReason: sp.documentationRejectReason ?? '',
    });
  }, []);

  const updateForm = useCallback(
    <K extends keyof SelectiveProcessForm>(key: K, value: SelectiveProcessForm[K]) => {
      setForm(prev => ({ ...prev, [key]: value }));
    },
    [],
  );

  const { mutate, isPending: isSaving } =
    usePutApiV1CourseregistrationEnrollmentCourseregistrationidSelectiveProcessTab({
      mutation: {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey:
              getApiV1CourseregistrationEnrollmentCourseregistrationidSelectiveProcessTabQueryKey(
                enrollmentId,
              ),
          });
          onSuccess();
        },
      },
    });

  const save = useCallback(() => {
    mutate({
      courseRegistrationId: enrollmentId,
      data: {
        status: SELECTIVE_STATUS_TO_CODE[form.status],
        admissionType: ADMISSION_TYPE_TO_CODE[form.admissionType],
        isEntranceExam: form.isEntranceExam,
        isAttendEntranceExam: form.attendedEntranceExam,
        entranceExamDate: form.entranceExamDate || null,
        interviewDate: form.interviewDate || null,
        isDocumentationApproved: form.isDocumentationApproved,
        documentationRejectionReasonId: null,
        entranceExamGrade:
          form.entranceExamGrade !== '' ? Number(form.entranceExamGrade) * 10 : null,
        essayGrade: form.essayGrade !== '' ? Number(form.essayGrade) * 10 : null,
      },
    });
  }, [mutate, enrollmentId, form]);

  return { form, initForm, updateForm, save, isSaving } as const;
}
